import { type ReactNode } from 'react'

import { cn } from '../lib/cn'

/**
 * Shared line-icon set, ported verbatim from `designs/system.jsx`. Icons are
 * decorative (`aria-hidden`) — the accessible name lives on the parent control.
 */
const PATHS = {
  pin: (
    <>
      <path d="M12 22s7-7.6 7-13a7 7 0 1 0-14 0c0 5.4 7 13 7 13z" />
      <circle cx="12" cy="9" r="2.5" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </>
  ),
  badge: (
    <>
      <circle cx="12" cy="9" r="6" />
      <path d="M9 14l-1 7 4-2 4 2-1-7" />
    </>
  ),
  chat: <path d="M4 5h16v11H8l-4 4z" />,
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </>
  ),
  chevr: <path d="m9 6 6 6-6 6" />,
  chevl: <path d="m15 6-6 6 6 6" />,
  chevd: <path d="m6 9 6 6 6-6" />,
  x: (
    <>
      <path d="M6 6l12 12" />
      <path d="M6 18 18 6" />
    </>
  ),
  check: <path d="m5 12 5 5L20 7" />,
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v.01M11 12h1v5h1" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 1 1 8 0v3" />
    </>
  ),
  phone: (
    <path d="M5 4h3l2 5-2 1a11 11 0 0 0 6 6l1-2 5 2v3a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" />
  ),
  sos: (
    <>
      <path d="M12 2 2 22h20L12 2z" />
      <path d="M12 10v5M12 18v.01" />
    </>
  ),
  bed: (
    <>
      <path d="M3 18V8M3 14h18v4M21 18v-4a3 3 0 0 0-3-3H10v3" />
      <circle cx="6.5" cy="11.5" r="1.5" />
    </>
  ),
  // Ported from designs/system.jsx alongside the hospital card (M17-T06): the
  // card's distance line pairs a locality pin with this navigation target.
  nav: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 3v2M12 19v2M3 12h2M19 12h2" />
      <circle cx="12" cy="12" r="2" />
    </>
  ),
  pill: (
    <>
      <rect
        x="3"
        y="9"
        width="18"
        height="6"
        rx="3"
        transform="rotate(-30 12 12)"
      />
      <path d="M9.5 7.5l-3 5.2" />
    </>
  ),
  flask: (
    <>
      <path d="M9 3h6M10 3v5L4.5 18A2 2 0 0 0 6.2 21h11.6a2 2 0 0 0 1.7-3L14 8V3" />
      <path d="M7 15h10" />
    </>
  ),
  image: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="9" cy="10" r="1.5" />
      <path d="m3 17 5-4 5 4 3-2 5 4" />
    </>
  ),
  knife: <path d="M14 4 4 14l3 3 13-3z" />,
  // M34-T04 (ADR-0063): Transport's tile. A car, not the truck, which read as
  // an ambulance next to the Emergency tile.
  car: (
    <>
      <path d="M3 13l2.2-5A2 2 0 0 1 7 6.8h10a2 2 0 0 1 1.8 1.2l2.2 5v4h-2.5" />
      <path d="M5.5 17H3v-4h18M9.5 17h5" />
      <circle cx="7.5" cy="17" r="2" />
      <circle cx="16.5" cy="17" r="2" />
    </>
  ),
  truck: (
    <>
      <rect x="2" y="7" width="11" height="9" rx="1" />
      <path d="M13 10h5l3 3v3h-8M5 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM17 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" />
    </>
  ),
  heart: (
    <path d="M12 20s-7-4.6-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.4-7 10-7 10z" />
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  moon: <path d="M20 14A8 8 0 1 1 10 4a7 7 0 0 0 10 10z" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  fast: <path d="m13 2-9 12h7l-1 8 9-12h-7l1-8z" />,
  filter: <path d="M3 5h18l-7 9v5l-4 1v-6L3 5z" />,
  arrow: (
    <>
      <path d="M5 12h14" />
      <path d="m13 5 7 7-7 7" />
    </>
  ),
  wifi: (
    <>
      <path d="M2 9a16 16 0 0 1 20 0" />
      <path d="M5 13a11 11 0 0 1 14 0" />
      <path d="M8.5 16.5a6 6 0 0 1 7 0" />
      <circle cx="12" cy="20" r="1" />
    </>
  ),
} as const

export type IconName = keyof typeof PATHS

export interface IconProps {
  name: IconName
  size?: number
  className?: string
}

export function Icon({ name, size = 16, className }: IconProps): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cn(className)}
    >
      {PATHS[name]}
    </svg>
  )
}
